import type { InfiniteQueryKeyData, InfiniteQueryParams, Pagination } from '@pba/react-query';
import { type MessageViewDataObject, isMessagePlaceholder } from '#models/services';
import { useInfiniteQuery } from '#react-query';
import { getChatroomMessages } from '../../services';
import { type GetMessagesQueryFilter, getMessagesInfiniteQueryKey } from './keys';

export type GetMessagesQueryParams = {
  pagination?: Pick<Pagination, 'pageSize'>;
} & Omit<
  InfiniteQueryParams<MessagesInfiniteQueryData, GetMessagesQueryFilter, never, string | undefined>,
  'pagination'
>;

export type MessagesInfiniteQueryData = InfiniteQueryKeyData<
  MessageViewDataObject[],
  typeof getMessagesInfiniteQueryKey
>;

export const useGetMessagesInfinite = ({ filter, options, pagination }: GetMessagesQueryParams) => {
  const pageSize = pagination?.pageSize ?? 25;

  return useInfiniteQuery({
    async queryFn({ pageParam }) {
      const response = await getChatroomMessages({
        config: {
          params: {
            $limit: pageSize,
            $sort: {
              createdAt: '-1',
            },
            createdAt: pageParam
              ? {
                  $lt: pageParam,
                }
              : undefined,
            type: 'message',
          },
        },
        routeParams: {
          chatroomId: filter.chatroomId,
        },
      });

      return response.data.data;
    },
    getNextPageParam(lastPage) {
      const messages = lastPage.filter((message) => !isMessagePlaceholder(message));

      return messages.length < pageSize ? undefined : messages.at(-1)?.createdAt;
    },
    initialPageParam: undefined,
    queryKey: getMessagesInfiniteQueryKey({ filter }),
    ...options,
  });
};
